import React, { useState, useCallback, useEffect, ReactNode, useRef } from 'react';
import { ThemeProvider, createTheme, CssBaseline } from '@mui/material';
import { Box, Drawer, List, ListItem, ListItemIcon, ListItemText, AppBar, Toolbar, Typography, IconButton, Button, Dialog, DialogTitle, DialogContent, DialogActions, Select, MenuItem, FormControl, InputLabel, SelectChangeEvent, Grid, CircularProgress, Divider } from '@mui/material';
import { MoneyOff, TableChart, Dashboard, Description, AddCircle, Menu as MenuIcon, AttachMoney, ShowChart, AccountBalance, Groups, PhoneAndroid, WbSunny, TrendingUp } from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { DragDropContext, Droppable, Draggable, DropResult } from 'react-beautiful-dnd';
import { IntlProvider, FormattedMessage, useIntl } from 'react-intl';
import ptMessages from './locales/pt-PT.json';
import enMessages from './locales/en-US.json';
import ExpenseForm from './components/ExpenseForm';
import FilteredExpenses from './components/FilteredExpenses';
import MonthlyExpenses from './components/MonthlyExpenses';
import ExpenseReport from './components/ExpenseReport';
import SharedExpenses from './components/SharedExpenses';
import DashboardComponent from './components/Dashboard';
import { BenjaminEtfs } from './components/BenjaminEtfs';
import { LoadingSkeleton } from './components/LoadingSkeleton';
import { Expense } from './types/Expense';
import { addExpense, getExpenses, updateExpense, deleteExpense, updateCategory, updatePayerColor, getPayerColors, initializePayerColors, updateIncomeCategory, getCategoryIcons, updateCategoryIcon } from './firebase/firestoreOperations';
import { signOut, User } from "firebase/auth";
import { auth } from "./firebase/config";
import Login from './components/Login';
import { generateRandomColor } from './utils/colorUtils';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { db } from './firebase/config';
import { SketchPicker } from 'react-color';
import { AuthProvider } from './contexts/AuthContext';
import { Income } from './types/Income';
import { addIncome, getIncome, updateIncome, deleteIncome } from './firebase/firestoreOperations';
import IncomeForm from './components/IncomeForm';
import IncomeList from './components/IncomeList';


import { onAuthStateChanged } from "firebase/auth";
import { BrowserRouter as Router, Route, Routes, Link } from 'react-router-dom';
import InvestmentList from './components/InvestmentList';
import MobileInvestments from './components/MobileInvestments';
import BankAccounts from './components/BankAccounts';
import Sidebar from './components/Sidebar';
import { onSnapshot, query, where, collection } from "firebase/firestore";
import { expensesCache, incomeCache } from './firebase/firestoreOperations';
import CalendarView from './components/CalendarView';
import { CalendarMonth } from '@mui/icons-material';
import { Timestamp } from 'firebase/firestore';
import { designTokens } from './theme/designTokens';
import { normalizeAllExpenses } from './firebase/normalizationOperations';
import { exportAllData, importAllData } from './services/backupService';
import { SolarPanelsDashboard } from './components/SolarPanelsDashboard';
import { useNotification } from './contexts/NotificationContext';

// Definição dos temas modernizados
const themes = {
  default: {
    primary: { main: designTokens.colors.primary.main },
    secondary: { main: designTokens.colors.success.main },
  },
  dark: {
    primary: { main: '#475569' },
    secondary: { main: designTokens.colors.error.main },
  },
  light: {
    primary: { main: '#64748b' },
    secondary: { main: '#94a3b8' },
  },
  nature: {
    primary: { main: designTokens.colors.success.main },
    secondary: { main: designTokens.colors.warning.main },
  },
  ocean: {
    primary: { main: '#0ea5e9' },
    secondary: { main: '#f97316' },
  },
  custom: {
    primary: { main: '#000000' },
    secondary: { main: '#ffffff' },
  },
};

type ThemeName = keyof typeof themes;

const drawerWidth = 240;

const messages: { [key: string]: any } = {
  'pt-PT': ptMessages,
  'en-US': enMessages,
};

interface AppProps {
  locale: 'pt-PT' | 'en-US';
  setLocale: (locale: 'pt-PT' | 'en-US') => void;
}

const App: React.FC<AppProps> = ({ locale, setLocale }) => {
  const intl = useIntl();
  const { notifySuccess, notifyError, notifyWarning, notifyInfo } = useNotification();
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [expenseCategories, setExpenseCategories] = useState<string[]>([]);
  const [incomeCategories, setIncomeCategories] = useState<string[]>([]);
  const [payers, setPayers] = useState<string[]>([]);
  const [visiblePayers, setVisiblePayers] = useState<string[]>([]);
  const [previousState, setPreviousState] = useState<{ expenses: Expense[], categories: string[], payers: string[] } | null>(null);
  const [activeTab, setActiveTab] = useState('add');
  const [mobileOpen, setMobileOpen] = useState(false);
  const [menuItems, setMenuItems] = useState<Array<{ id: string; text: string; icon: ReactNode; value: string; }>>([
    { id: 'dashboard', text: intl.formatMessage({ id: 'menu.dashboard' }), icon: <Dashboard />, value: 'dashboard' },
    { id: 'add', text: intl.formatMessage({ id: 'menu.add' }), icon: <AddCircle />, value: 'add' },
    { id: 'monthly', text: intl.formatMessage({ id: 'menu.monthly' }), icon: <MoneyOff />, value: 'monthly' },
    { id: 'calendar', text: intl.formatMessage({ id: 'menu.calendar' }), icon: <CalendarMonth />, value: 'calendar' },
    { id: 'all', text: intl.formatMessage({ id: 'menu.all' }), icon: <TableChart />, value: 'all' },
    { id: 'report', text: intl.formatMessage({ id: 'menu.report' }), icon: <Description />, value: 'report' },
    { id: 'income', text: intl.formatMessage({ id: 'menu.income' }), icon: <AttachMoney />, value: 'income' },
    { id: 'investments', text: intl.formatMessage({ id: 'menu.investments' }), icon: <ShowChart />, value: 'investments' },
    { id: 'benjaminEtfs', text: intl.formatMessage({ id: 'menu.benjaminEtfs' }), icon: <TrendingUp />, value: 'benjaminEtfs' },
    { id: 'solarPanels', text: intl.formatMessage({ id: 'menu.solarPanels' }), icon: <WbSunny />, value: 'solarPanels' },
    { id: 'mobileInvestments', text: intl.formatMessage({ id: 'menu.mobileInvestments' }), icon: <PhoneAndroid />, value: 'mobileInvestments' },
    { id: 'bankAccounts', text: intl.formatMessage({ id: 'menu.bankAccounts' }), icon: <AccountBalance />, value: 'bankAccounts' },
  ]);
  const [openSettings, setOpenSettings] = useState(false);
  const [themeMode, setThemeMode] = useState<'light' | 'dark' | 'system'>(() => {
    return localStorage.getItem('themeMode') as 'light' | 'dark' | 'system' || 'light';
  });
  const [themeName, setThemeName] = useState<ThemeName>(() => {
    return localStorage.getItem('themeName') as ThemeName || 'default';
  });
  const [customPrimaryColor, setCustomPrimaryColor] = useState(() => {
    return localStorage.getItem('customPrimaryColor') || '#000000';
  });
  const [customSecondaryColor, setCustomSecondaryColor] = useState(() => {
    return localStorage.getItem('customSecondaryColor') || '#ffffff';
  });
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [key, setKey] = useState(0);
  const [payerColors, setPayerColors] = useState<{ [key: string]: string }>({});
  const [categoryIcons, setCategoryIcons] = useState<{ [key: string]: string }>({});
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState<string | null>(null);
  const [income, setIncome] = useState<Income[]>([]);
  const [filterYear, setFilterYear] = useState<number>(new Date().getFullYear());
  const [filterMonth, setFilterMonth] = useState<number | 'all'>('all');
  const [filterPayer, setFilterPayer] = useState<string>('');
  const [isNormalizing, setIsNormalizing] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      setLoading(false);
      if (currentUser) {
        setActiveTab('add');
        setOpenSettings(false);
        fetchUserData(currentUser);
        fetchIncome(currentUser);
      } else {
      }
    });

    return () => unsubscribe();
  }, []);

  const fetchUserData = async (user: User) => {
    try {
      await initializePayerColors();
      const fetchedExpenses = await getExpenses(user);
      const fetchedIncome = await getIncome(user);
      setExpenses(fetchedExpenses);
      setIncome(fetchedIncome);

      const expenseCategories = new Set(fetchedExpenses.map(e => e.category));
      const incomeCategories = new Set(fetchedIncome.map(i => i.category));
      setExpenseCategories(Array.from(expenseCategories));
      setIncomeCategories(Array.from(incomeCategories));

      const uniquePayers = Array.from(new Set(fetchedExpenses.flatMap(e => [e.payer, e.sharedWith]).filter((payer): payer is string => Boolean(payer))));
      setPayers(uniquePayers);
      setVisiblePayers(uniquePayers);

      const colors = await getPayerColors();
      setPayerColors(colors);

      const icons = await getCategoryIcons();
      setCategoryIcons(icons);
    } catch (error) {
      console.error("Error fetching data:", error);
      if (error instanceof Error) {
        setAuthError(`Failed to fetch user data: ${error.message}`);
      } else {
        setAuthError("Failed to fetch user data: Unknown error");
      }
    }
  };

  const fetchIncome = async (user: User) => {
    try {
      if (!user) {
        console.error("No authenticated user");
        setAuthError("No authenticated user");
        return;
      }
      const fetchedIncome = await getIncome(user);
      setIncome(fetchedIncome);
    } catch (error) {
      console.error("Error fetching income:", error);
      if (error instanceof Error) {
        console.error("Error name:", error.name);
        console.error("Error message:", error.message);
        console.error("Error stack:", error.stack);
        setAuthError(`Failed to fetch income data: ${error.message}`);
      } else {
        console.error("Unknown error type:", error);
        setAuthError("Failed to fetch income data: Unknown error");
      }
    }
  };

  const addExpenseHandler = useCallback(async (expense: Omit<Expense, 'id' | 'userId'>, file?: File) => {
    if (user) {
      try {
        const newExpense = await addExpense({ ...expense, userId: user.uid }, user, file);
        setExpenses(prevExpenses => {
          const exists = prevExpenses.some(e => e.id === newExpense.id);
          return exists ? prevExpenses : [...prevExpenses, newExpense];
        });
        setExpenseCategories(prevCategories =>
          prevCategories.includes(newExpense.category)
            ? prevCategories
            : [...prevCategories, newExpense.category]
        );

        if (!payers.includes(newExpense.payer)) {
          setPayers(prevPayers => [...prevPayers, newExpense.payer]);
          setVisiblePayers(prevVisible => [...prevVisible, newExpense.payer]);
        }

        if (newExpense.shared && newExpense.sharedWith && !payers.includes(newExpense.sharedWith)) {
          setPayers(prevPayers => [...prevPayers, newExpense.sharedWith!]);
          setVisiblePayers(prevVisible => [...prevVisible, newExpense.sharedWith!]);
        }

      } catch (error) {
        console.error("Error in addExpenseHandler:", error);
        if (error instanceof Error) {
          console.error("Error message:", error.message);
          console.error("Error stack:", error.stack);
        }
        notifyError(`Failed to add expense: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    }
  }, [payers, user]);

  const editExpenseHandler = useCallback(async (editedExpense: Expense) => {
    if (user) {
      try {
        await updateExpense(editedExpense, user);
        setExpenses(prevExpenses => prevExpenses.map(expense =>
          expense.id === editedExpense.id ? editedExpense : expense
        ));

        if (!expenseCategories.includes(editedExpense.category)) {
          setExpenseCategories(prevCategories => [...prevCategories, editedExpense.category]);
        }

        const newPayers = new Set(payers);
        newPayers.add(editedExpense.payer);
        if (editedExpense.shared && editedExpense.sharedWith) {
          newPayers.add(editedExpense.sharedWith);
        }
        setPayers(Array.from(newPayers));

        if (editedExpense.shared && editedExpense.sharedWith) {
          const sharedExpense: Omit<Expense, 'id'> = {
            ...editedExpense,
            payer: editedExpense.sharedWith,
            sharedWith: editedExpense.payer,
            amount: editedExpense.amount / 2,
          };

          const existingSharedExpense = expenses.find(e =>
            e.date === editedExpense.date &&
            e.category === editedExpense.category &&
            e.description === editedExpense.description &&
            e.payer === editedExpense.sharedWith &&
            e.sharedWith === editedExpense.payer
          );

          if (existingSharedExpense) {
            await updateExpense({ ...sharedExpense, id: existingSharedExpense.id }, user);
            setExpenses(prevExpenses => prevExpenses.map(expense =>
              expense.id === existingSharedExpense.id ? { ...sharedExpense, id: existingSharedExpense.id } : expense
            ));
          }
        }

      } catch (error) {
        console.error("Error editing expense:", error);
        notifyError(`Failed to edit expense: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    }
  }, [expenseCategories, payers, expenses, user]);

  const deleteExpenseHandler = useCallback(async (id: string) => {
    if (user) {
      await deleteExpense(id, user);
      setExpenses(prevExpenses => prevExpenses.filter(expense => expense.id !== id));
    }
  }, [user]);

  const addExpenseCategory = useCallback((newCategory: string) => {
    setExpenseCategories(prevCategories =>
      prevCategories.includes(newCategory)
        ? prevCategories
        : [...prevCategories, newCategory]
    );
  }, []);

  const addIncomeCategory = useCallback((newCategory: string) => {
    setIncomeCategories(prevCategories =>
      prevCategories.includes(newCategory)
        ? prevCategories
        : [...prevCategories, newCategory]
    );
  }, []);

  const editExpenseCategory = useCallback(async (oldCategory: string, newCategory: string) => {
    try {
      await updateCategory(oldCategory, newCategory);
      setExpenseCategories(prevCategories => {
        const updatedCategories = prevCategories.filter(c => c !== oldCategory);
        return [...updatedCategories, newCategory].sort();
      });
      setExpenses(prevExpenses =>
        prevExpenses.map(expense =>
          expense.category === oldCategory ? { ...expense, category: newCategory } : expense
        )
      );
      // Update icon mapping when category is renamed
      if (categoryIcons[oldCategory]) {
        const iconName = categoryIcons[oldCategory];
        setCategoryIcons(prevIcons => {
          const newIcons = { ...prevIcons };
          newIcons[newCategory] = iconName;
          delete newIcons[oldCategory];
          return newIcons;
        });
        // Update in Firestore
        await updateCategoryIcon(newCategory, iconName);
        if (oldCategory !== newCategory) {
          // Clear old icon mapping
          const oldIcons = { ...categoryIcons };
          delete oldIcons[oldCategory];
          await updateCategoryIcon(oldCategory, '').catch(() => { }); // Clear old icon
        }
      }
    } catch (error) {
      console.error("Error updating category:", error);
    }
  }, [categoryIcons]);

  const deleteExpenseCategory = useCallback(async (categoryToDelete: string) => {
    setExpenseCategories(prevCategories => prevCategories.filter(c => c !== categoryToDelete));
    setExpenses(prevExpenses => prevExpenses.map(expense =>
      expense.category === categoryToDelete ? { ...expense, category: 'Uncategorized' } : expense
    ));
    // Remove icon when category is deleted
    if (categoryIcons[categoryToDelete]) {
      setCategoryIcons(prevIcons => {
        const newIcons = { ...prevIcons };
        delete newIcons[categoryToDelete];
        return newIcons;
      });
      // Remove from Firestore
      try {
        await updateCategoryIcon(categoryToDelete, '');
      } catch (error) {
        console.error("Error removing category icon:", error);
      }
    }
  }, [categoryIcons]);

  const editCategoryIcon = useCallback(async (category: string, iconName: string) => {
    try {
      await updateCategoryIcon(category, iconName);
      setCategoryIcons(prevIcons => ({ ...prevIcons, [category]: iconName }));
    } catch (error) {
      console.error("Error updating category icon:", error);
    }
  }, []);

  const addPayer = useCallback((newPayer: string) => {
    const trimmedPayer = newPayer.trim();
    if (!payers.includes(trimmedPayer)) {
      setPreviousState({ expenses, categories: expenseCategories, payers });
      setPayers(prevPayers => [...prevPayers, trimmedPayer]);
      setVisiblePayers(prevVisible => [...prevVisible, trimmedPayer]);

      const newColor = generateRandomColor();
      setPayerColors(prevColors => ({ ...prevColors, [trimmedPayer]: newColor }));

      updatePayerColor(trimmedPayer, newColor);
    } else {
      notifyWarning(intl.formatMessage({ id: 'payerExists' }, { payer: trimmedPayer }));
    }
  }, [expenses, expenseCategories, payers, intl]);

  const editPayer = useCallback((oldPayer: string, newPayer: string) => {
    const trimmedNewPayer = newPayer.trim();
    if (oldPayer !== trimmedNewPayer && !payers.includes(trimmedNewPayer)) {
      setPreviousState({ expenses, categories: expenseCategories, payers });
      setPayers(prevPayers =>
        prevPayers.map(p => p === oldPayer ? trimmedNewPayer : p)
      );
      setVisiblePayers(prevVisible =>
        prevVisible.map(p => p === oldPayer ? trimmedNewPayer : p)
      );
      setExpenses(prevExpenses =>
        prevExpenses.map(expense => ({
          ...expense,
          payer: expense.payer === oldPayer ? trimmedNewPayer : expense.payer,
          sharedWith: expense.sharedWith === oldPayer ? trimmedNewPayer : expense.sharedWith
        }))
      );
    } else if (payers.includes(trimmedNewPayer)) {
      notifyWarning(intl.formatMessage({ id: 'payerExists' }, { payer: trimmedNewPayer }));
    }
  }, [expenses, expenseCategories, payers, intl]);

  const deletePayer = useCallback((payerToDelete: string) => {
    if (payers.length <= 1) {
      notifyWarning(intl.formatMessage({ id: 'cantDeleteLastPayer' }));
      return;
    }
    if (window.confirm(intl.formatMessage({ id: 'confirmDeletePayer' }, { payer: payerToDelete }))) {
      setPreviousState({ expenses, categories: expenseCategories, payers });
      setPayers(prevPayers => prevPayers.filter(p => p !== payerToDelete));
      setVisiblePayers(prevVisible => prevVisible.filter(p => p !== payerToDelete));
      setExpenses(prevExpenses =>
        prevExpenses.map(expense => {
          if (expense.payer === payerToDelete) {
            return { ...expense, payer: payers[0] };
          }
          if (expense.sharedWith === payerToDelete) {
            return { ...expense, sharedWith: payers[0] === payerToDelete ? payers[1] : payers[0] };
          }
          return expense;
        })
      );
    }
  }, [expenses, expenseCategories, payers, intl]);

  const handleClearAllData = () => {
    if (window.confirm(intl.formatMessage({ id: 'confirmClearData' }))) {
      setPreviousState({ expenses, categories: expenseCategories, payers });
      setExpenses([]);
      setExpenseCategories([]);
      setPayers([]);
      setVisiblePayers([]);
    }
  };

  const handleUndo = () => {
    if (previousState) {
      setExpenses(previousState.expenses);
      setExpenseCategories(previousState.categories);
      setPayers(previousState.payers);
      setPreviousState(null);
    }
  };

  const togglePayerVisibility = useCallback((payer: string) => {
    setVisiblePayers(prevVisible =>
      prevVisible.includes(payer)
        ? prevVisible.filter(p => p !== payer)
        : [...prevVisible, payer]
    );
  }, []);

  const handleNormalizeData = async () => {
    if (user && window.confirm(intl.formatMessage({ id: 'normalizeData.confirm' }))) {
      try {
        setIsNormalizing(true);
        const result = await normalizeAllExpenses(user);
        if (result.totalUpdated > 0) {
          // Re-fetch expenses to update UI
          const fetchedExpenses = await getExpenses(user);
          setExpenses(fetchedExpenses);
          notifySuccess(intl.formatMessage({ id: 'normalizeData.success' }, { count: result.totalUpdated }));
        } else {
          notifyInfo(intl.formatMessage({ id: 'normalizeData.noChanges' }));
        }
      } catch (error) {
        console.error("Normalization error:", error);
        notifyError("Error normalizing data.");
      } finally {
        setIsNormalizing(false);
      }
    }
  };

  const handleExportData = async () => {
    if (!user) return;
    try {
      setIsExporting(true);
      await exportAllData(user.uid);
      notifySuccess(intl.formatMessage({ id: 'backup.exportSuccess' }));
    } catch (error) {
      notifyError(intl.formatMessage({ id: 'backup.exportError' }, { error: error instanceof Error ? error.message : 'Unknown error' }));
    } finally {
      setIsExporting(false);
    }
  };

  const handleImportData = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!user || !event.target.files?.length) return;
    const file = event.target.files[0];

    if (!file.name.endsWith('.json')) {
      notifyWarning(intl.formatMessage({ id: 'backup.invalidFile' }));
      return;
    }

    if (!window.confirm(intl.formatMessage({ id: 'backup.importConfirm' }))) {
      // Reset file input
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    try {
      setIsImporting(true);
      const result = await importAllData(user.uid, file);
      notifySuccess(intl.formatMessage({ id: 'backup.importSuccess' }, {
        expenses: result.expenses,
        income: result.income,
        investments: result.investments,
        bankAccounts: result.bankAccounts,
      }));
      // Refresh all data
      await fetchUserData(user);
      await fetchIncome(user);
    } catch (error) {
      notifyError(intl.formatMessage({ id: 'backup.importError' }, { error: error instanceof Error ? error.message : 'Unknown error' }));
    } finally {
      setIsImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const onDragEnd = (result: DropResult) => {
    if (!result.destination) {
      return;
    }

    const items = Array.from(menuItems);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    setMenuItems(items);
    localStorage.setItem('menuOrder', JSON.stringify(items.map(item => item.id)));
  };

  const handleOpenSettings = () => {
    setOpenSettings(true);
  };

  const handleCloseSettings = () => {
    setOpenSettings(false);
  };

  const handleThemeChange = (event: SelectChangeEvent<'light' | 'dark' | 'system'>) => {
    const newThemeMode = event.target.value as 'light' | 'dark' | 'system';
    setThemeMode(newThemeMode);
    localStorage.setItem('themeMode', newThemeMode);
  };

  const handleThemeNameChange = (event: SelectChangeEvent<ThemeName>) => {
    const newThemeName = event.target.value as ThemeName;
    setThemeName(newThemeName);
    localStorage.setItem('themeName', newThemeName);
  };

  const handleCustomColorChange = (color: string, type: 'primary' | 'secondary') => {
    if (type === 'primary') {
      setCustomPrimaryColor(color);
      localStorage.setItem('customPrimaryColor', color);
    } else {
      setCustomSecondaryColor(color);
      localStorage.setItem('customSecondaryColor', color);
    }
  };

  const theme = React.useMemo(
    () =>
      createTheme({
        palette: {
          mode: themeMode === 'system' ? (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light') : themeMode,
          primary: themeName === 'custom'
            ? { main: customPrimaryColor }
            : themes[themeName].primary,
          secondary: themeName === 'custom'
            ? { main: customSecondaryColor }
            : themes[themeName].secondary,
          background: {
            default: themeMode === 'dark' || (themeMode === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches)
              ? designTokens.colors.background.default
              : '#f8fafc',
            paper: themeMode === 'dark' || (themeMode === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches)
              ? designTokens.colors.background.paper
              : '#ffffff',
          },
          text: {
            primary: themeMode === 'dark' || (themeMode === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches)
              ? designTokens.colors.text.primary
              : '#0f172a',
            secondary: themeMode === 'dark' || (themeMode === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches)
              ? designTokens.colors.text.secondary
              : '#475569',
          },
        },
        typography: {
          fontFamily: designTokens.typography.fontFamily,
          fontSize: 14,
          h1: { fontWeight: designTokens.typography.fontWeight.bold, fontSize: designTokens.typography.fontSize['4xl'] },
          h2: { fontWeight: designTokens.typography.fontWeight.bold, fontSize: designTokens.typography.fontSize['3xl'] },
          h3: { fontWeight: designTokens.typography.fontWeight.semibold, fontSize: designTokens.typography.fontSize['2xl'] },
          h4: { fontWeight: designTokens.typography.fontWeight.semibold, fontSize: designTokens.typography.fontSize.xl },
          h5: { fontWeight: designTokens.typography.fontWeight.medium, fontSize: designTokens.typography.fontSize.lg },
          h6: { fontWeight: designTokens.typography.fontWeight.medium, fontSize: designTokens.typography.fontSize.base },
          body1: { fontSize: designTokens.typography.fontSize.base, lineHeight: designTokens.typography.lineHeight.normal },
          body2: { fontSize: designTokens.typography.fontSize.sm, lineHeight: designTokens.typography.lineHeight.normal },
        },
        shape: {
          borderRadius: parseInt(designTokens.borderRadius.md),
        },
        components: {
          MuiCard: {
            styleOverrides: {
              root: {
                borderRadius: designTokens.borderRadius.lg,
                boxShadow: designTokens.shadows.md,
                transition: `all ${designTokens.transitions.normal}`,
                '&:hover': {
                  boxShadow: designTokens.shadows.lg,
                },
              },
            },
          },
          MuiButton: {
            styleOverrides: {
              root: {
                borderRadius: designTokens.borderRadius.md,
                textTransform: 'none',
                fontWeight: designTokens.typography.fontWeight.medium,
                transition: `all ${designTokens.transitions.fast}`,
              },
            },
          },
          MuiTextField: {
            styleOverrides: {
              root: {
                '& .MuiOutlinedInput-root': {
                  borderRadius: designTokens.borderRadius.md,
                },
              },
            },
          },
        },
      }),
    [themeMode, themeName, customPrimaryColor, customSecondaryColor]
  );

  const drawer = (
    <div>
      <Toolbar />
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="menu">
          {(provided) => (
            <List {...provided.droppableProps} ref={provided.innerRef}>
              {menuItems.map((item, index) => (
                <Draggable key={item.id} draggableId={item.id} index={index}>
                  {(provided, snapshot) => (
                    <ListItem
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      {...provided.dragHandleProps}
                      button
                      onClick={() => setActiveTab(item.value)}
                      selected={activeTab === item.value}
                    >
                      <ListItemIcon>{item.icon}</ListItemIcon>
                      <ListItemText primary={intl.formatMessage({ id: `menu.${item.id}` })} />
                    </ListItem>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </List>
          )}
        </Droppable>
      </DragDropContext>
    </div>
  );

  useEffect(() => {
    const savedOrder = localStorage.getItem('menuOrder');
    if (savedOrder) {
      const orderIds = JSON.parse(savedOrder) as string[];
      const orderedItems = orderIds
        .map(id => menuItems.find(item => item.id === id))
        .filter(Boolean) as typeof menuItems;
      if (orderedItems.length === menuItems.length) {
        setMenuItems(orderedItems);
      }
    }
  }, []);

  useEffect(() => {
    setMenuItems(prevItems => prevItems.map(item => ({
      ...item,
      text: intl.formatMessage({ id: `menu.${item.id}` })
    })));
  }, [intl]);

  useEffect(() => {
    setKey(prevKey => prevKey + 1);
  }, [locale]);

  const editPayerColorHandler = useCallback(async (payer: string, color: string) => {
    try {
      await updatePayerColor(payer, color);
      setPayerColors(prev => ({ ...prev, [payer]: color }));
    } catch (error) {
      console.error("Error updating payer color:", error);
      notifyError(`Failed to update payer color: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }, []);

  const handleLogout = async () => {
    try {
      await signOut(auth);
      setUser(null);
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  useEffect(() => {
    if (user) {
      loadUserSettings();
    }
  }, [user]);

  const loadUserSettings = () => {
    const savedThemeMode = localStorage.getItem('themeMode') as 'light' | 'dark' | 'system';
    const savedThemeName = localStorage.getItem('themeName') as ThemeName;
    const savedCustomPrimaryColor = localStorage.getItem('customPrimaryColor');
    const savedCustomSecondaryColor = localStorage.getItem('customSecondaryColor');

    if (savedThemeMode) setThemeMode(savedThemeMode);
    if (savedThemeName) setThemeName(savedThemeName);
    if (savedCustomPrimaryColor) setCustomPrimaryColor(savedCustomPrimaryColor);
    if (savedCustomSecondaryColor) setCustomSecondaryColor(savedCustomSecondaryColor);
  };

  const saveUserSettings = () => {
    localStorage.setItem('themeMode', themeMode);
    localStorage.setItem('themeName', themeName);
    localStorage.setItem('customPrimaryColor', customPrimaryColor);
    localStorage.setItem('customSecondaryColor', customSecondaryColor);
  };

  const handleUpdateExpense = async (updatedExpense: Expense) => {
    if (user) {
      try {
        await updateExpense(updatedExpense, user);
        setExpenses(prevExpenses =>
          prevExpenses.map(expense =>
            expense.id === updatedExpense.id ? updatedExpense : expense
          )
        );
      } catch (error) {
        console.error("Error updating expense:", error);
        // Adicione uma notificação de erro aqui, se desejar
      }
    }
  };

  const addIncomeHandler = useCallback(async (incomeEntry: Omit<Income, 'id' | 'userId'>, file?: File) => {
    if (user) {
      try {
        const newIncome = await addIncome(incomeEntry, user.uid, file);
        setIncome(prevIncome => {
          const exists = prevIncome.some(i => i.id === newIncome.id);
          return exists ? prevIncome : [...prevIncome, newIncome];
        });
        setIncomeCategories(prevCategories =>
          prevCategories.includes(newIncome.category)
            ? prevCategories
            : [...prevCategories, newIncome.category]
        );
      } catch (error) {
        console.error("Error in addIncomeHandler:", error);
        notifyError(`Failed to add income: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    }
  }, [user, incomeCategories]);

  const editIncomeHandler = useCallback(async (updatedIncome: Income): Promise<void> => {
    if (user) {
      try {
        await updateIncome(updatedIncome, user);
        setIncome(prevIncome => prevIncome.map(inc => inc.id === updatedIncome.id ? updatedIncome : inc));
      } catch (error) {
        console.error("Error in editIncomeHandler:", error);
        notifyError(`Failed to update income: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    }
  }, [user]);

  const deleteIncomeHandler = useCallback(async (id: string) => {
    if (user) {
      try {
        await deleteIncome(id, user);
        setIncome(prevIncome => prevIncome.filter(inc => inc.id !== id));
      } catch (error) {
        console.error("Error in deleteIncomeHandler:", error);
        notifyError(`Failed to delete income: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    }
  }, [user]);

  const editIncomeCategoryHandler = useCallback(async (oldCategory: string, newCategory: string) => {
    if (user) {
      try {
        // Atualizar a categoria no Firestore (você precisa implementar esta função)
        await updateIncomeCategory(oldCategory, newCategory, user);

        // Atualizar o estado local
        setIncomeCategories(prevCategories => {
          const updatedCategories = prevCategories.filter(c => c !== oldCategory);
          return [...updatedCategories, newCategory].sort();
        });

        setIncome(prevIncome =>
          prevIncome.map(inc =>
            inc.category === oldCategory ? { ...inc, category: newCategory } : inc
          )
        );
      } catch (error) {
        console.error("Error updating income category:", error);
        notifyError(`Failed to update income category: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    }
  }, [user]);

  useEffect(() => {
  }, [menuItems]);

  useEffect(() => {
    if (user) {
      const expensesQuery = query(collection(db, "expenses"), where("userId", "==", user.uid));
      const unsubscribeExpenses = onSnapshot(expensesQuery, (snapshot) => {
        const updatedExpenses = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          date: doc.data().date.toDate().toISOString().split('T')[0]
        } as Expense));
        setExpenses(updatedExpenses);
        expensesCache.set(user.uid, updatedExpenses);
      });

      const incomeQuery = query(collection(db, 'income'), where("userId", "==", user.uid));
      const unsubscribeIncome = onSnapshot(incomeQuery, (snapshot) => {
        const updatedIncome = snapshot.docs.map(doc => ({
          ...doc.data(),
          id: doc.id,
          date: doc.data().date instanceof Timestamp ? doc.data().date.toDate().toISOString().split('T')[0] : doc.data().date,
        } as Income));
        setIncome(updatedIncome);
        incomeCache.set(user.uid, updatedIncome);
      });

      return () => {
        unsubscribeExpenses();
        unsubscribeIncome();
      };
    }
  }, [user]);

  if (loading) {
    return <LoadingSkeleton />;
  }

  if (!user) {
    return <Login setUser={setUser} />;
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{ display: 'flex' }} key={key}>
        <AppBar
          position="fixed"
          sx={{
            zIndex: (theme) => theme.zIndex.drawer + 1,
            display: { sm: 'none' }
          }}
        >
          <Toolbar>
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
            <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
              <FormattedMessage id="app.title" />
            </Typography>
          </Toolbar>
        </AppBar>
        <Sidebar
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          menuItems={menuItems}
          setMenuItems={setMenuItems}
          mobileOpen={mobileOpen}
          handleDrawerToggle={handleDrawerToggle}
          drawerWidth={drawerWidth}
          user={user}
          onOpenSettings={handleOpenSettings}
        />
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: { xs: activeTab === 'mobileInvestments' ? 0 : 2, sm: 3 },
            width: { xs: '100%', sm: `calc(100% - ${drawerWidth}px)` },
            maxWidth: '100vw',
            minWidth: 0,
            overflowX: 'hidden',
            marginLeft: { sm: `${drawerWidth}px` },
            marginTop: { xs: '64px', sm: 0 },
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            minHeight: { xs: 'calc(100vh - 64px)', sm: '100vh' },
          }}
        >
          <Box sx={{ width: '100%', maxWidth: '1200px' }}>
            <AnimatePresence mode="wait">
              <motion.div
                key={activeTab}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
                style={{ width: '100%' }}
              >
                {activeTab === 'dashboard' && (
                  <DashboardComponent
                    expenses={expenses.filter(e => e.userId === user.uid)}
                    income={income.filter(i => i.userId === user.uid)}
                    payers={payers}
                    payerColors={payerColors}
                    categoryIcons={categoryIcons}
                  />
                )}
                {activeTab === 'bankAccounts' && (
                  <BankAccounts user={user} />
                )}


                {activeTab === 'add' && (
                  <ExpenseForm
                    onAddExpense={addExpenseHandler}
                    categories={expenseCategories}
                    payers={payers}
                    onAddCategory={addExpenseCategory}
                    onEditCategory={editExpenseCategory}
                    onDeleteCategory={deleteExpenseCategory}
                    onAddPayer={addPayer}
                    onEditPayer={editPayer}
                    onDeletePayer={deletePayer}
                    onEditPayerColor={editPayerColorHandler}
                    payerColors={payerColors}
                    categoryIcons={categoryIcons}
                    onEditCategoryIcon={editCategoryIcon}
                    expenses={expenses.filter(e => e.userId === user.uid)}
                  />
                )}
                {activeTab === 'all' && (
                  <FilteredExpenses
                    expenses={expenses.filter(e => e.userId === user.uid)}
                    filter={{
                      year: filterYear,
                      month: filterMonth,
                      payer: filterPayer
                    }}
                    payers={payers}
                    categories={expenseCategories}
                    onEditExpense={editExpenseHandler}
                    onDeleteExpense={deleteExpenseHandler}
                    payerColors={payerColors}
                    categoryIcons={categoryIcons}
                  />
                )}
                {activeTab === 'monthly' && (
                  <MonthlyExpenses
                    expenses={expenses.filter(e => e.userId === user.uid)}
                    payers={payers}
                    categories={expenseCategories}
                    payerColors={payerColors}
                    categoryIcons={categoryIcons}
                  />
                )}
                {activeTab === 'calendar' && (
                  <CalendarView
                    expenses={expenses.filter(e => e.userId === user.uid)}
                    categories={expenseCategories}
                    payers={payers}
                    payerColors={payerColors}
                    categoryIcons={categoryIcons}
                    locale={locale}
                  />
                )}
                {activeTab === 'sharedExpenses' && (
                  <SharedExpenses
                    expenses={expenses.filter(e => e.userId === user.uid)}
                    payers={payers}
                    payerColors={payerColors}
                    onUpdateExpense={handleUpdateExpense}
                    onAddExpense={addExpenseHandler}
                  />
                )}
                {activeTab === 'report' && (
                  <ExpenseReport
                    expenses={expenses.filter(e => e.userId === user.uid)}
                    payers={payers}
                    payerColors={payerColors}
                    onUpdateExpense={handleUpdateExpense}
                  />
                )}
                {activeTab === 'income' && (
                  <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                      <IncomeForm
                        onAddIncome={addIncomeHandler}
                        categories={incomeCategories}
                        onAddCategory={addIncomeCategory}
                        onEditCategory={editIncomeCategoryHandler}
                      />
                      <IncomeList
                        income={income}
                        onEditIncome={editIncomeHandler}
                        onDeleteIncome={deleteIncomeHandler}
                        categories={incomeCategories}
                        onAddCategory={addIncomeCategory}
                        onEditCategory={editIncomeCategoryHandler}
                      />
                    </Box>
                  </Box>
                )}
                {activeTab === 'investments' && (
                  <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                      <InvestmentList
                        user={user}
                      />
                    </Box>
                  </Box>
                )}
                {activeTab === 'benjaminEtfs' && user && (
                  <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                      <BenjaminEtfs
                        user={user}
                      />
                    </Box>
                  </Box>
                )}
                {activeTab === 'solarPanels' && (
                  <SolarPanelsDashboard expenses={expenses} />
                )}
                {activeTab === 'mobileInvestments' && (
                  <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                      <MobileInvestments
                        user={user}
                      />
                    </Box>
                  </Box>
                )}
              </motion.div>
            </AnimatePresence>
          </Box>
        </Box>

        {openSettings && (
          <Dialog open={openSettings} onClose={handleCloseSettings}>
            <DialogTitle>
              <FormattedMessage id="settings" />
            </DialogTitle>
            <DialogContent>
              <FormControl fullWidth margin="normal">
                <InputLabel id="language-select-label">
                  <FormattedMessage id="language" />
                </InputLabel>
                <Select
                  labelId="language-select-label"
                  value={locale}
                  onChange={(e) => setLocale(e.target.value as 'pt-PT' | 'en-US')}
                  label={<FormattedMessage id="language" />}
                >
                  <MenuItem value="pt-PT">Português</MenuItem>
                  <MenuItem value="en-US">English</MenuItem>
                </Select>
              </FormControl>
              <FormControl fullWidth margin="normal">
                <InputLabel id="theme-mode-select-label"><FormattedMessage id="themeMode" /></InputLabel>
                <Select
                  labelId="theme-mode-select-label"
                  value={themeMode}
                  onChange={handleThemeChange}
                  label={<FormattedMessage id="themeMode" />}
                >
                  <MenuItem value="light"><FormattedMessage id="lightMode" /></MenuItem>
                  <MenuItem value="dark"><FormattedMessage id="darkMode" /></MenuItem>
                  <MenuItem value="system"><FormattedMessage id="systemMode" /></MenuItem>
                </Select>
              </FormControl>
              <FormControl fullWidth margin="normal">
                <InputLabel id="theme-name-select-label"><FormattedMessage id="themeName" /></InputLabel>
                <Select
                  labelId="theme-name-select-label"
                  value={themeName}
                  onChange={handleThemeNameChange}
                  label={<FormattedMessage id="themeName" />}
                >
                  <MenuItem value="default"><FormattedMessage id="defaultTheme" /></MenuItem>
                  <MenuItem value="dark"><FormattedMessage id="darkTheme" /></MenuItem>
                  <MenuItem value="light"><FormattedMessage id="lightTheme" /></MenuItem>
                  <MenuItem value="nature"><FormattedMessage id="natureTheme" /></MenuItem>
                  <MenuItem value="ocean"><FormattedMessage id="oceanTheme" /></MenuItem>
                  <MenuItem value="custom"><FormattedMessage id="customTheme" /></MenuItem>
                </Select>
              </FormControl>
              {themeName === 'custom' && (
                <Box mt={2}>
                  <Button
                    onClick={() => setShowColorPicker(!showColorPicker)}
                    variant="outlined"
                    fullWidth
                  >
                    <FormattedMessage id="chooseCustomColors" />
                  </Button>
                  {showColorPicker && (
                    <Box mt={2}>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <Typography><FormattedMessage id="primaryColor" /></Typography>
                          <Box mt={1} mb={2}>
                            <SketchPicker
                              color={customPrimaryColor}
                              onChangeComplete={(color) => handleCustomColorChange(color.hex, 'primary')}
                              disableAlpha
                            />
                          </Box>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography><FormattedMessage id="secondaryColor" /></Typography>
                          <Box mt={1} mb={2}>
                            <SketchPicker
                              color={customSecondaryColor}
                              onChangeComplete={(color) => handleCustomColorChange(color.hex, 'secondary')}
                              disableAlpha
                            />
                          </Box>
                        </Grid>
                      </Grid>
                    </Box>
                  )}
                </Box>
              )}
              <Box mt={2}>
                <Button
                  onClick={handleNormalizeData}
                  color="primary"
                  fullWidth
                  variant="outlined"
                  disabled={isNormalizing}
                  startIcon={isNormalizing ? <CircularProgress size={20} /> : undefined}
                >
                  <FormattedMessage id={isNormalizing ? "normalizeData.processing" : "normalizeData"} />
                </Button>
              </Box>
              <Box mt={3}>
                <Divider sx={{ mb: 2 }} />
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold', color: 'text.secondary' }}>
                  <FormattedMessage id="backup.sectionTitle" />
                </Typography>
                <Button
                  onClick={handleExportData}
                  color="primary"
                  fullWidth
                  variant="outlined"
                  disabled={isExporting || isImporting}
                  startIcon={isExporting ? <CircularProgress size={20} /> : undefined}
                  sx={{ mb: 1 }}
                >
                  <FormattedMessage id={isExporting ? "backup.exporting" : "backup.export"} />
                </Button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".json"
                  style={{ display: 'none' }}
                  onChange={handleImportData}
                />
                <Button
                  onClick={() => fileInputRef.current?.click()}
                  color="warning"
                  fullWidth
                  variant="outlined"
                  disabled={isExporting || isImporting}
                  startIcon={isImporting ? <CircularProgress size={20} /> : undefined}
                >
                  <FormattedMessage id={isImporting ? "backup.importing" : "backup.import"} />
                </Button>
              </Box>
              <Box mt={2}>
                <Button onClick={handleUndo} color="primary" fullWidth disabled={!previousState}>
                  <FormattedMessage id="undo" />
                </Button>
              </Box>
              <Box mt={2}>
                <Button onClick={handleLogout} color="error" fullWidth>
                  <FormattedMessage id="logout" />
                </Button>
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseSettings} color="primary">
                <FormattedMessage id="close" />
              </Button>
            </DialogActions>
          </Dialog>
        )}
      </Box>
    </ThemeProvider >
  );
};

export default App;